const SEARCH_FILTERS_STORAGE_KEY = 'virtco-search-filters';
const SEARCH_TEXT_STORAGE_KEY = 'virtco-search-text';
const PERSIST_ROOT_KEY = 'persist:root';

export const searchFiltersHaveValues = (filters: any): boolean => {
  if (!filters) return false;
  const hasValues = (value: any) => (Array.isArray(value) ? value.length > 0 : Boolean(value));
  return hasValues(filters.selectedCategories)
    || hasValues(filters.selectedSubCategories)
    || hasValues(filters.location)
    || hasValues(filters.language)
    || hasValues(filters.availability)
    || hasValues(filters.pricing)
    || hasValues(filters.ratings);
};

export const readStoredSearchFilters = (): { filters: any | null; known: boolean } => {
  try {
    const raw = sessionStorage.getItem(SEARCH_FILTERS_STORAGE_KEY);
    if (raw == null) return { filters: null, known: false };
    if (raw === 'null') return { filters: null, known: true };
    const parsed = JSON.parse(raw);
    return searchFiltersHaveValues(parsed)
      ? { filters: parsed, known: true }
      : { filters: null, known: true };
  } catch {
    return { filters: null, known: false };
  }
};

export const writeStoredSearchFilters = (filters: any | null) => {
  try {
    if (!searchFiltersHaveValues(filters)) {
      sessionStorage.setItem(SEARCH_FILTERS_STORAGE_KEY, 'null');
      return;
    }
    sessionStorage.setItem(SEARCH_FILTERS_STORAGE_KEY, JSON.stringify(filters));
  } catch {
    /* storage can be unavailable */
  }
};

export const readStoredSearchText = (): { text: string; known: boolean } => {
  try {
    const raw = sessionStorage.getItem(SEARCH_TEXT_STORAGE_KEY);
    if (raw == null) return { text: '', known: false };
    return { text: raw, known: true };
  } catch {
    return { text: '', known: false };
  }
};

export const writeStoredSearchText = (text: string) => {
  try {
    sessionStorage.setItem(SEARCH_TEXT_STORAGE_KEY, text || '');
  } catch {
    /* storage can be unavailable */
  }
};

type SearchConsultationPersistState = {
  filters?: any;
  searchText?: string;
  lastExploreTimestamp?: number | null;
  lastDetailTimestamp?: number | null;
};

// redux-persist waits before writing. A refresh in that gap reloads the previous search text.
export const syncPersistedSearchConsultation = (next: SearchConsultationPersistState) => {
  try {
    const raw = localStorage.getItem(PERSIST_ROOT_KEY);
    if (!raw) return;
    const root = JSON.parse(raw);
    const current = root.searchConsultation ? JSON.parse(root.searchConsultation) : {};
    if (next.searchText !== undefined) current.searchText = next.searchText;
    if (next.filters !== undefined) current.filters = next.filters;
    if (next.lastExploreTimestamp !== undefined) current.lastExploreTimestamp = next.lastExploreTimestamp;
    if (next.lastDetailTimestamp !== undefined) current.lastDetailTimestamp = next.lastDetailTimestamp;
    root.searchConsultation = JSON.stringify(current);
    localStorage.setItem(PERSIST_ROOT_KEY, JSON.stringify(root));
  } catch {
    /* storage can be unavailable */
  }
};

// One write for sessionStorage and persist:root. Store middleware calls this after search actions.
export const rememberSearchConsultation = (state: SearchConsultationPersistState | null | undefined) => {
  const filters = state?.filters ?? null;
  const searchText = state?.searchText ?? '';
  writeStoredSearchFilters(filters);
  writeStoredSearchText(searchText);
  syncPersistedSearchConsultation({
    filters,
    searchText,
    lastExploreTimestamp: state?.lastExploreTimestamp ?? null,
    lastDetailTimestamp: state?.lastDetailTimestamp ?? null,
  });
};
